import React, { useEffect } from "react";
import styles from "./Frame.module.css";
import Monogram from "assets/images/monogram.png";
import PhotoFrame from "assets/images/frame.png";
import ButtonImg from "assets/images/btn.png";
import Outline from "assets/images/outline.png";
import Ellipse4 from "../../assets/images/ellipse4.png";
import { useImageUpload } from "hooks/useImageUpload";
import { useDrag } from "hooks/useTransform/useDrag";
import { useZoom } from "hooks/useTransform/useZoom";

const DOWNLOAD_SIZE = 1080;

const Frame = () => {
    const {
        selectedImage,
        buttonText,
        fileInputRef,
        handleImageUpload,
        handleDeletePhoto
    } = useImageUpload()

    const {
        offsetX,
        offsetY,
        isDragging,
        handleMouseDown,
    } = useDrag()

    const {
        scale,
        frameRef,
        handleTouchStart,
        initialDistance
    } = useZoom()

    useEffect(() => {
        const disableScroll = (e: Event) => {
            e.preventDefault();
        };

        if (isDragging || initialDistance > 0) {
            document.addEventListener("touchmove", disableScroll, { passive: false });
        } else {
            document.removeEventListener("touchmove", disableScroll);
        }

        return () => {
            document.removeEventListener("touchmove", disableScroll);
        };
    }, [isDragging, initialDistance]);

    useEffect(() => {
        const preventZoom = (e: Event) => {
            if ((e as TouchEvent).touches.length > 1) {
                e.preventDefault();
            }
        };

        document.addEventListener("gesturestart", preventZoom as EventListener, {passive: false});
        document.addEventListener("gesturechange", preventZoom as EventListener, {passive: false});

        return () => {
            document.removeEventListener("gesturestart", preventZoom as EventListener);
            document.removeEventListener("gesturechange", preventZoom as EventListener);
        };
    }, []);

    const handleTouchStartCombined = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
        handleTouchStart(e);
    } else if (e.touches.length === 1) {
        handleMouseDown(e);
    }
};

    const handleDownload = () => {
        if (!selectedImage) return;

        const frameElement = frameRef.current;
        if (!frameElement) return;

        const frameRect = frameElement.getBoundingClientRect();
        const frameWidth = frameRect.width;
        const frameHeight = frameRect.height;

        const scaleFactorX = DOWNLOAD_SIZE / frameWidth;
        const scaleFactorY = DOWNLOAD_SIZE / frameHeight;

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        canvas.width = DOWNLOAD_SIZE;
        canvas.height = DOWNLOAD_SIZE;

        const image = new Image();
        image.src = selectedImage;

        const frame = new Image();
        frame.src = PhotoFrame;

        image.onload = () => {
            frame.onload = () => {
                if (!ctx) return;

                const imgAspect = image.width / image.height;
                const frameAspect = frameWidth / frameHeight;

                let drawWidth, drawHeight;

                if (imgAspect > frameAspect) {
                    drawWidth = frameWidth * scaleFactorX * scale;
                    drawHeight = drawWidth / imgAspect;
                } else {
                    drawHeight = frameHeight * scaleFactorY * scale;
                    drawWidth = drawHeight * imgAspect;
                }

                const imgX = (DOWNLOAD_SIZE - drawWidth) / 2 + (offsetX * scaleFactorX);
                const imgY = (DOWNLOAD_SIZE - drawHeight) / 2 + (offsetY * scaleFactorY);

                ctx.drawImage(image, imgX, imgY, drawWidth, drawHeight);
                ctx.drawImage(frame, 0, 0, DOWNLOAD_SIZE, DOWNLOAD_SIZE);

                const link = document.createElement("a");
                link.href = canvas.toDataURL("image/png");
                link.download = "downloaded-image.png";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            };
        };
    };

    return (
        <section className={styles['frame-section']}>
            <img src={Ellipse4} alt="Decorative Ellipse" className={styles.ellipse4} />
            <div className={styles['frame-content']}>
                <div className={styles['left-content']}>
                    <img src={Monogram} alt="Logo" className={styles['logo-image']} />
                    <div className={`${styles['text-container']} ${styles.hidden}`}>
                        <p className={styles.subtitle}>Weekend</p>
                        <p className={styles.subtitle}>with Dmitriy Grechnev</p>
                    </div>
                </div>
                <div className={styles['right-content']}>
                    <div className={styles['photo-frame']} ref={frameRef}>
                        <div className={styles['image-container']}>
                            {selectedImage && (
                                <img
                                    src={selectedImage}
                                    alt="Uploaded"
                                    className={styles["uploaded-image"]}
                                    draggable="false"
                                    style={{ transform: `translate(${offsetX}px, ${offsetY}px) scale(${scale})` }}
                                    onMouseDown={handleMouseDown}
                                     onTouchStart={handleTouchStartCombined}
                                />
                            )}
                        </div>
                        <img src={PhotoFrame} alt="Photo Frame" className={`${styles.frame} ${!selectedImage ? styles['white-background'] : ''}`}  />
                        <img src={Outline} alt="Decorative Frame" className={styles['outline']}/>
                    </div>
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} id="fileInput"/>
                    <div className={styles['button-container']}>
                        <button className={styles['upload-button']} onClick={selectedImage ? handleDownload : () => document.getElementById("fileInput")?.click()}>
                            <img src={ButtonImg} alt="Button Image" className={styles["button-img"]} />
                            <span className={styles['button-text']}>{buttonText}</span>
                        </button>
                        <button className={styles['delete-button']} onClick={handleDeletePhoto} disabled={!selectedImage}>
                            <img src={ButtonImg} alt="Button Image" className={styles['button-img']} />
                            <span className={styles['button-text']}>delete photo</span>
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Frame;